import { useMemo } from 'react'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { Anchor, Checkbox, Group, Badge } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { tableDefaultProps } from '@shared/components/table/table'
import { NoDataMessage } from '@shared/components/table/table-no-data-message'
import { OfferContactOption } from '@loom-api-types'
import { OfferEditorData } from '@/pages/offers/create-offer/create-offer.page'

interface OfferRecipientsTableProps {
  data: OfferContactOption[]
  renderKey?: string
  form?: UseFormReturnType<OfferEditorData, (values: OfferEditorData) => OfferEditorData> | null
  noDataMessage?: string
  isLoading?: boolean
}

const getContactName = (contact: OfferContactOption) =>
  [contact.firstName, contact.lastName].filter((name) => !!name?.trim()).join(' ') ||
  'No name provided'

export const OfferRecipientsTable = ({
  data = [],
  renderKey = '',
  form = null,
  noDataMessage = 'No contacts available',
  isLoading,
}: OfferRecipientsTableProps) => {
  const columns = useMemo<MRT_ColumnDef<OfferContactOption>[]>(
    () => [
      {
        id: 'fullName',
        accessorKey: 'fullName',
        header: 'Full Name',
        Cell: ({ row }) => {
          const name = getContactName(row.original)
          if (row.original.crmLink) {
            return (
              <Anchor href={row.original.crmLink} target='_blank' size='sm'>
                {name}
              </Anchor>
            )
          }
          return name
        },
      },
      {
        id: 'roles',
        accessorKey: 'roles',
        header: 'Role(s)',
        Cell: ({ cell }) => (
          <Group gap='xs'>
            {cell.getValue<string[]>()?.map((value) => (
              <Badge tt='capitalize' color='gray.2' c='black' key={value}>
                {value}
              </Badge>
            ))}
          </Group>
        ),
      },
      {
        id: 'email',
        accessorKey: 'email',
        header: 'Email',
      },
      {
        id: 'mobilePhone',
        accessorKey: 'mobilePhone',
        header: 'Phone',
      },
      {
        id: 'sendEmail',
        accessorKey: 'sendEmail',
        header: 'Email',
        Cell: ({ row }) => (
          <Checkbox
            disabled={!row.original.email || row.original.email === ''}
            {...form?.getInputProps(`offerContactSelections.${row.index}.sendEmail`, {
              type: 'checkbox',
            })}
            key={form?.key(`offerContactSelections.${row.index}.sendEmail`)}
            aria-label={`Send emails to ${getContactName(row.original)}`}
            error={
              !!form?.errors?.offerContactSelections ||
              !!form?.errors[`offerContactSelections.${row.index}`]
            }
          />
        ),
      },
      {
        id: 'sendSms',
        accessorKey: 'sendSms',
        header: 'SMS',
        Cell: ({ row }) => (
          <Checkbox
            disabled={!row.original.mobilePhone || row.original.mobilePhone === ''}
            {...form?.getInputProps(`offerContactSelections.${row.index}.sendSms`, {
              type: 'checkbox',
            })}
            key={form?.key(`offerContactSelections.${row.index}.sendSms`)}
            aria-label={`Send sms to ${getContactName(row.original)}`}
            error={
              !!form?.errors?.offerContactSelections ||
              !!form?.errors[`offerContactSelections.${row.index}`]
            }
          />
        ),
      },
      {
        id: 'sendReminders',
        accessorKey: 'sendReminders',
        header: 'Reminders',
        Cell: ({ row }) => (
          <Checkbox
            {...form?.getInputProps(`offerContactSelections.${row.index}.sendReminders`, {
              type: 'checkbox',
            })}
            key={form?.key(`offerContactSelections.${row.index}.sendReminders`)}
            aria-label={`Send reminders to ${getContactName(row.original)}`}
          />
        ),
      },
    ],
    [form?.errors]
  )

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data,
    state: {
      isLoading,
    },
    renderEmptyRowsFallback: () => <NoDataMessage noDataMessage={noDataMessage} />,
    mantineTableProps: {
      ...tableDefaultProps.mantineTableProps,
      'aria-label': 'Offer recipients',
    },
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
  })

  return <MantineReactTable table={table} key={renderKey} />
}
